import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
} from '@material-ui/core';
import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from 'react-query';
import { Close, Delete } from '@material-ui/icons';
import Autocomplete from '@material-ui/lab/Autocomplete';
import NumberFormat from 'react-number-format';
import request from '../../../../../../services/network/request';
import { useAuth } from '../../../../../../auth/authProvider';
import { formatPrice } from '../../../../../../helpers/utils';

function ajustaDatasParaCiclo(dataInicio) {
  const inicio = new Date(dataInicio);
  const dataInicioCiclo = new Date(inicio.getFullYear(), inicio.getMonth(), 15);
  const dataFimCiclo = new Date(inicio.getFullYear(), inicio.getMonth() + 1, 15);
  const pad = n => n.toString().padStart(2, '0');
  const format = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return {
    dataInicio: format(dataInicioCiclo),
    dataFim: format(dataFimCiclo),
  };
}

const AdicaoRegrasCampanhas = ({ open, onClose, regrasCampanhas, setRegrasCampanhas, classes, setFormData }) => {
  const { token } = useAuth();
  const [errors, setErrors] = useState({});
  const [currentRule, setCurrentRule] = useState({
    distributors: [],
    rules: [],
  });

  const handleChange = e => {
    const { name, value } = e.target;
    setCurrentRule(prev => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleRemoveDistribuidora = useCallback(acronym => {
    setCurrentRule(prev => ({
      ...prev,
      distributors: prev.distributors.filter(item => item !== acronym),
    }));
  }, []);

  const FIVE_MINUTES = 300000;

  const { data: energyUtilities = [] } = useQuery(
    ['fetch-dx'],
    () => request.get('discount-rules/energy-utilities', { token }),
    {
      staleTime: FIVE_MINUTES,
      cacheTime: FIVE_MINUTES * 2,
      enabled: open,
    },
  );

  const distribuidorasOptions = useMemo(
    () =>
      (energyUtilities || [])
        .filter(option => option && option.acronym)
        .sort((a, b) => a.acronym.localeCompare(b.acronym)),
    [energyUtilities],
  );

  const valueDistribuidoras = useMemo(
    () => distribuidorasOptions.filter(option => currentRule.distributors.includes(option.acronym)),
    [distribuidorasOptions, currentRule.distributors],
  );

  const canAddRule = () => {
    if ((regrasCampanhas.rules || []).length === 0) {
      return (
        currentRule.mwMinValue !== '' &&
        Array.isArray(currentRule.distributors) &&
        currentRule.distributors.length > 0 &&
        currentRule.discountAmount !== '' &&
        (currentRule.mwMaxValue === '' || Number(currentRule.mwMaxValue) >= Number(currentRule.mwMinValue))
      );
    }
    return (
      currentRule.mwMinValue !== '' &&
      currentRule.discountAmount !== '' &&
      (currentRule.mwMaxValue === '' || Number(currentRule.mwMaxValue) >= Number(currentRule.mwMinValue))
    );
  };

  const handleAddRule = () => {
    if (currentRule.mwMaxValue !== '' && Number(currentRule.mwMaxValue) < Number(currentRule.mwMinValue)) {
      setErrors(prev => ({ ...prev, mwMaxValue: 'O valor final não pode ser menor que o inicial.' }));
      return;
    }
    if (canAddRule()) {
      setRegrasCampanhas(prev => ({
        ...prev,
        distributors: currentRule.distributors,
        rules: [
          ...(prev.rules || []),
          {
            mwMinValue: currentRule.mwMinValue,
            mwMaxValue: currentRule.mwMaxValue,
            discountAmount: currentRule.discountAmount,
          },
        ],
      }));
      setCurrentRule(prev => ({
        ...prev,
        mwMinValue: '',
        mwMaxValue: '',
        discountAmount: '',
      }));
      setErrors(prev => ({ ...prev, mwMaxValue: '' }));
    }
  };

  const handleRemoveRule = ruleIdx => {
    setRegrasCampanhas(prev => ({
      ...prev,
      rules: prev.rules.filter((_, i) => i !== ruleIdx),
    }));
  };

  const handleAddRegra = () => {
    setFormData(prev => {
      const novoFormData = { ...prev };
      if (!prev.activationRules || prev.activationRules.length === 0) {
        // Só ajusta se for o primeiro activationRules
        const { dataInicio, dataFim } = ajustaDatasParaCiclo(prev.dataInicio);
        novoFormData.dataInicio = dataInicio;
        novoFormData.dataFim = dataFim;
        if (typeof window !== 'undefined' && window.setSnackbar) {
          window.setSnackbar({
            open: true,
            message: `Período ajustado automaticamente para o ciclo: início em 15/${dataInicio.split('-')[1]}/${
              dataInicio.split('-')[0]
            } e fim em 15/${dataFim.split('-')[1]}/${dataFim.split('-')[0]}`,
          });
        }
      }
      return {
        ...novoFormData,
        activationRules: [...(prev.activationRules || []), { ...regrasCampanhas }],
      };
    });
    setRegrasCampanhas(prev => ({
      ...prev,
      rules: [
        ...(prev.rules || []),
        {
          mwMinValue: currentRule.mwMinValue,
          mwMaxValue: currentRule.mwMaxValue,
          discountAmount: currentRule.discountAmount,
        },
      ],
    }));
    onClose();
  };

  const canAddRegra = () => {
    return Array.isArray(regrasCampanhas.rules) && regrasCampanhas.rules.length > 0;
  };

  React.useEffect(() => {
    if (!Array.isArray(regrasCampanhas.distributors)) {
      setRegrasCampanhas(prev => ({ ...prev, distributors: [] }));
    }
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    setRegrasCampanhas(prev => ({
      ...prev,
      distributors: currentRule.distributors,
    }));
  }, [currentRule.distributors]);

  React.useEffect(() => {
    if (!open) {
      setCurrentRule({
        distributors: [],
        rules: [],
        mwMinValue: '',
        mwMaxValue: '',
        discountAmount: '',
      });
      setErrors({});
      setRegrasCampanhas({
        distributors: [],
        rules: [],
      });
    }
  }, [open]);

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>
        <Typography variant="h6">Adicionar regras</Typography>
      </DialogTitle>
      <DialogContent style={{ maxHeight: '50vh', overflow: 'auto' }}>
        {Array.isArray(regrasCampanhas.rules) && regrasCampanhas.rules.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2">Distribuidoras: {regrasCampanhas.distributors?.join(', ')}</Typography>
            <List>
              {regrasCampanhas.rules.map((rule, ruleIdx) => (
                <React.Fragment key={`rule-${rule.mwMinValue}-${rule.mwMaxValue}-${rule.discountAmount}`}>
                  <ListItem>
                    <ListItemText
                      primary={`${rule.mwMinValue} MWh ${rule.mwMaxValue ? `até ${rule.mwMaxValue} MWh` : 'ou mais'}`}
                      secondary={`R$ ${formatPrice(rule.discountAmount)}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => handleRemoveRule(ruleIdx)}>
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {ruleIdx < regrasCampanhas.rules.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}

        <Box mb={2}>
          {currentRule.distributors.length > 0 && (
            <Box mb={2}>
              <Typography variant="subtitle2">Distribuidoras selecionadas:</Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                {currentRule.distributors.map(acronym => (
                  <Box
                    key={acronym}
                    display="flex"
                    alignItems="center"
                    mr={1}
                    mb={1}
                    style={{ background: '#f0f0f0', borderRadius: 4, padding: '2px 8px' }}
                  >
                    <Typography variant="caption">{acronym}</Typography>
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() => handleRemoveDistribuidora(acronym)}
                      style={{ minWidth: 0, marginLeft: 4, padding: 0 }}
                    >
                      <Close style={{ width: 16, height: 16 }} />
                    </Button>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          <Box style={{ background: '#f0f0f0', borderRadius: 4, padding: 12 }}>
            <FormControl
              fullWidth
              error={!!errors.distributors}
              style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8 }}
            >
              <Autocomplete
                multiple
                id="distributors-autocomplete"
                options={distribuidorasOptions}
                getOptionLabel={option => option.acronym}
                value={valueDistribuidoras}
                onChange={(event, newValue) => {
                  setCurrentRule(prev => ({
                    ...prev,
                    distributors: newValue.map(opt => opt.acronym),
                  }));
                  if (errors.distributors) {
                    setErrors(prev => ({ ...prev, distributors: '' }));
                  }
                }}
                ListboxProps={{
                  style: {
                    maxHeight: 200,
                    paddingTop: 0,
                    paddingBottom: 0,
                  },
                }}
                autoHighlight
                autoSelect={false}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Adicionar Distribuidora"
                    variant="outlined"
                    error={!!errors.distributors}
                    helperText={errors.distributors}
                    required
                  />
                )}
                style={{ flex: 1 }}
              />
              {errors.distributors && <FormHelperText>{errors.distributors}</FormHelperText>}
            </FormControl>

            <Box className={classes.boxStyle} style={{ marginTop: '16px', marginBottom: '16px' }}>
              <TextField
                fullWidth
                label="Somatório inicial (MWh)"
                name="mwMinValue"
                value={currentRule.mwMinValue}
                onChange={handleChange}
                type="number"
                error={!!errors.mwMinValue}
                helperText={errors.mwMinValue}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                required
              />

              <TextField
                fullWidth
                label="Somatório final (MWh)"
                name="mwMaxValue"
                value={currentRule.mwMaxValue}
                onChange={handleChange}
                type="number"
                error={
                  !!errors.mwMaxValue ||
                  (currentRule.mwMaxValue !== '' && Number(currentRule.mwMaxValue) < Number(currentRule.mwMinValue))
                }
                helperText={
                  errors.mwMaxValue ||
                  (currentRule.mwMaxValue !== '' && Number(currentRule.mwMaxValue) < Number(currentRule.mwMinValue)
                    ? 'O valor final não pode ser menor que o inicial.'
                    : '')
                }
                placeholder="Deixe em branco para 'sem limite'"
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
            </Box>

            <NumberFormat
              customInput={TextField}
              fullWidth
              label="Valor do Desconto (R$)"
              name="discountAmount"
              value={currentRule.discountAmount}
              onValueChange={values => setCurrentRule(prev => ({ ...prev, discountAmount: values.floatValue }))}
              thousandSeparator="."
              decimalSeparator=","
              prefix="R$ "
              error={!!errors.discountAmount}
              helperText={errors.discountAmount}
              style={{ marginTop: '8px' }}
              required
              allowNegative={false}
              isNumericString
            />
            <Box display="flex" justifyContent="flex-end">
              <Button color="primary" onClick={handleAddRule} disabled={!canAddRule()} style={{ marginTop: '16px' }}>
                Adicionar regra
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions style={{ marginBottom: '8px', marginRight: '8px' }}>
        <Button color="primary" onClick={onClose}>
          Cancelar
        </Button>
        <Button color="primary" onClick={handleAddRegra} disabled={!canAddRegra()}>
          Salvar regras
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdicaoRegrasCampanhas;
